import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

// Per product (GSE-12325 AC update 2026-10-09): "If user clicks 'Access It Here'
// button, user is redirected back to ShipGo.com homepage".
const EXISTING_BOOKING_URL = 'https://www.shipgo.com/';

const ShipGoWelcomeModal = ({ isOpen, onClose }) => {
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleExistingBookingClick = () => {
    handleClose();
    window.location.href = EXISTING_BOOKING_URL;
  };

  if (!isOpen) return null;

  return (
    <div
      // z-[1050] (Bootstrap modal baseline) outranks the legacy partner-page
      // site header (z-index: 1030) that was covering the modal's X and
      // heading on short phones. `overflow-y-auto` + `items-start sm:items-
      // center` keep the whole card reachable on viewports shorter than the
      // modal (e.g. iPhone 14 390x664 with Safari toolbars): mobile aligns
      // the modal to the top of the backdrop so the top of the card is
      // always visible; desktop keeps the Figma-faithful centered placement.
      className="fixed inset-0 z-[1050] flex items-start sm:items-center justify-center bg-black bg-opacity-50 p-[16px] overflow-y-auto"
      onClick={handleBackdropClick}
      data-testid="shipgo-modal-root"
    >
      <div
        className="bg-white rounded-[16px] shadow-[0_8px_16px_rgba(0,0,0,0.1)] w-full max-w-[480px] relative pt-[40px] pb-[24px] px-[20px] sm:pt-[48px] sm:pb-[32px] sm:px-[32px] font-['Poppins',sans-serif] my-auto"
      >
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-[8px] right-[8px] flex items-center justify-center w-[44px] h-[44px] rounded-[8px] bg-white text-[#1C2938]"
          aria-label="Close"
          data-testid="shipgo-modal-close"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div data-testid="shipgo-modal-heading">
          <p className="font-bold text-[22px] leading-[28px] sm:text-[28px] sm:leading-[32px] text-[#1C2938]">
            Looking for ShipGo?
          </p>
          <p className="font-bold text-[30px] leading-[36px] sm:text-[40px] sm:leading-[44px] mt-[8px] text-[#02A40B]">
            You&rsquo;re in the right place.
          </p>
        </div>

        <div className="mt-[32px] text-[16px] leading-[28px] text-[#1C2938]">
          <p className="font-semibold">
            Your favorite luggage shipping service now has a new home. Welcome to Ship&amp;Play.
          </p>
          <p className="mt-[16px]">Ship&amp;Play offers everything you loved about ShipGo, including:</p>
          <ul className="list-disc pl-[24px] mt-[8px]">
            <li>Door-to-door delivery</li>
            <li>Easy tracking</li>
            <li>The same knowledgeable, friendly support team</li>
          </ul>
        </div>

        <button
          type="button"
          onClick={handleClose}
          data-testid="shipgo-modal-cta"
          className="mt-[32px] w-full h-[56px] rounded-[8px] font-semibold text-[16px] leading-[28px] bg-[#48C44F] text-[#1C2938]"
        >
          Start Shipping
        </button>

        <p
          className="mt-[32px] text-center font-semibold text-[14px] leading-[24px] text-[#1C2938]"
          data-testid="shipgo-modal-fineprint"
        >
          Have an existing ShipGo booking?
        </p>
        <button
          type="button"
          onClick={handleExistingBookingClick}
          data-testid="shipgo-modal-existing-booking"
          className="mt-[8px] w-full h-[56px] rounded-[8px] font-semibold text-[16px] leading-[28px] bg-white border-2 text-[#1C2938] border-[#1C2938]"
        >
          Access It Here
        </button>
      </div>
    </div>
  );
};

ShipGoWelcomeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ShipGoWelcomeModal;
